import "./Avatar.scss";

import React, { useMemo } from "react";
import clsx from "clsx";
import runes from "runes";
import Ink from "react-ink";

import * as Utils from "../../../lib/util/Utils";
import Link from "../../nav/Link/Link";
import { Color } from "../../../lib/types/MiscTypes";
import { useTranslated } from "../../../lib/hook/LocaleHooks";
import { TextElement } from "../../../lib/util/LocaleUtils";

export interface AvatarProps {
	size?: number;
	background?: Color;
	name?: TextElement;
	className?: string;
	children?: React.ReactNode;
	guid?: string | number;
	style?: React.CSSProperties;
	href?: string;
	initials?: string;
	icon?: React.ReactElement | null;
}

const Avatar: React.FC<AvatarProps> = (props) => {
	let {
		size,
		background,
		name: propsName,
		className,
		children,
		guid,
		style,
		href,
		initials,
		icon,
		...otherProps
	} = props;

	const [translatedName] = useTranslated(propsName);

	// Specified colour or random
	const colorKey = guid || translatedName;

	background = background || (Utils.getColorName(colorKey) as any);

	size = size || 32;

	const computedInitials = useMemo(() => {
		let computedInitials = initials;
		if (!computedInitials && translatedName) {
			computedInitials = translatedName.trim();

			if (computedInitials.length > 2 && runes(computedInitials).length > 2) {
				computedInitials = computedInitials
					.toUpperCase()
					.split(" ")
					.filter((string) => string && !string.startsWith("("))
					.slice(0, 2)
					.map((part) => runes(part)[0])
					.join("");
			}
		}
		return computedInitials;
	}, [initials, translatedName]);

	const fontSize = `${size / 1.7}px`;
	const element = href ? Link : "div";

	const textStyle = useMemo(() => ({ fontSize }), [fontSize]);
	const avatarStyle = useMemo(
		() => ({
			...style,
			minWidth: `${size}px`,
			maxWidth: `${size}px`,
			minHeight: `${size}px`,
			maxHeight: `${size}px`,
		}),
		[size, style],
	);

	return React.createElement(
		element,
		{
			...otherProps,
			className: clsx("Avatar", background && `--bg-${background}`, className),
			href,
			style: avatarStyle,
		},
		<>
			{computedInitials && (
				<p className={"Avatar__initials"} style={textStyle}>
					{computedInitials}
				</p>
			)}
			{icon && icon}
			{href && <Ink />}
			{children}
		</>,
	);
};

export default React.memo(Avatar);
